import axios from 'axios';

const baseUrl = process.env.REACT_APP_DIFLEX_API_URL + "/tenants";

// Function to get all agreements for users
export async function getAllTenantsForUsers(apiAccessToken) {
  try {
    const response = await axios.get(`${baseUrl}/Tenants/get-tenants-for-user`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiAccessToken}`
      }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || 'Failed to fetch tenants');
  }
}

// Function to get all agreements for a user
export const getTenantsForUser = async (userId, apiAccessToken) => {
  try {
    const response = await axios.get(`${baseUrl}/tenants/${userId}`, {
      headers: {
        'Authorization': `Bearer ${apiAccessToken}`
      }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch tenants');
  }
};

// Function to get a specific agreement by agreementId
export async function getTenant(tenantPublicId, apiAccessToken) {
  try {
    const response = await axios.get(`${baseUrl}/Tenants/get-tenant/${encodeURIComponent(tenantPublicId)}`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiAccessToken}`
      }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch tenant');
  }
}

// Function to create a new agreement
export async function createTenant(tenantData, apiAccessToken) {
  try {
    const response = await axios.post(`${baseUrl}/create-tenant`, tenantData, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiAccessToken}`
      }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || 'Failed to create tenant');
  }
}

// Function to update an existing agreement
export async function updateTenant(tenantData, apiAccessToken) {
  try {
    const response = await axios.put(`${baseUrl}/update-tenant`, tenantData, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiAccessToken}`
      }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || 'Failed to update tenant');
  }
}

// Function to delete an agreement by agreementId
export async function deleteTenant(tenantPublicId, apiAccessToken) {
  try {
    const response = await axios.delete(`${baseUrl}/delete-agreement/${tenantPublicId}`, {
      headers: {
        'Authorization': `Bearer ${apiAccessToken}`
      }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || 'Failed to delete tenant');
  }
}


/**
 * Fetches the customer's SCO form questions (PBI #320), shown on the onboarding and
 * edit forms. The API only serves questions for the caller's own tenant.
 *
 * @param {string} tenantPublicId - Public id of the tenant
 * @param {string} apiAccessToken - Bearer token for the Insight API
 * @returns {Promise<object[]>} The tenant's active questions in render order
 * @throws {Error} If the API request fails
 */
export async function getScoCustomQuestions(tenantPublicId, apiAccessToken) {
  try {
    const response = await axios.get(`${baseUrl}/Tenants/sco-custom-questions/by-tenant/${encodeURIComponent(tenantPublicId)}`, {
      headers: {
        'Authorization': `Bearer ${apiAccessToken}`
      }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch SCO custom questions');
  }
}

/**
 * Fetches the values the customer's onboarding form offers beyond what its users already carry:
 * saved Office, Company and Department values (#357) and saved addresses (#358). Only served for
 * the caller's own tenant.
 *
 * @param {string} tenantPublicId - Public id of the tenant
 * @param {string} apiAccessToken - Bearer token for the Insight API
 * @returns {Promise<{listValues: object[], addresses: object[]}>} The saved values, hidden list values included
 * @throws {Error} If the API request fails
 */
export async function getScoFormValues(tenantPublicId, apiAccessToken) {
  try {
    const response = await axios.get(`${baseUrl}/ScoFormValues/by-tenant/${encodeURIComponent(tenantPublicId)}`, {
      headers: {
        'Authorization': `Bearer ${apiAccessToken}`
      }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch SCO form values');
  }
}
